import React, { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import s from './ImprovementPlan.module.scss';
import { FormattedMessage, injectIntl } from 'react-intl';
import LoadingSpinner from '../../LoadingSpinner/LoadingSpinner';
import Banner from '../../Banner/Banner';
import Pagination from '../../Pagination/Pagination';
import { DEFAULT_PAGE_SIZE, GENERAL_STATUS } from '../../../constants';
import Dropdown from '../../WSAControls/DroprdownContainer/Dropdown';
import ImprovementPlanListTable from './ImprovementPlanListTable';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { compareValues } from '../../../utils/';
import { getUserProfileWidget, getImprovementPlan, getGeneralData } from '../../../selectors/index';
import { WidgetVisibility, IsWidgetAccessible } from '../../../utils/widgetManager';
import { initialImprovementPlanState } from '../../../store/reducers/initialState';
import SiteDashboardControlsContentTemplate from '../../SiteDashboardControls/SiteDashboardControlsContentTemplate';
import Button from 'react-bootstrap/Button';
import { useHistory, useParams } from 'react-router-dom';
import ConfirmDialog from '../../ConfirmDialog/ConfirmDialog';
import SearchBar from '../../SearchBar/SearchBar';
import Cookies from 'js-cookie';
import {
  getImprovementPlanThunk,
  setFilteredDataThunk,
  setCurrentPage,
  setImprovementPlanChangeThunk,
  getFilterThunk,
  setPageFilter,
  saveImprovementPlanThunk
} from '../../../store/actions/hazardEvent/improvementPlan';
import Breadcrumb from '../../WSAControls/Breadcrumb';

const STATUS = 'STATUS';
const SEARCH_FILTER = 'SEARCH_FILTER';
const envName = process.env.REACT_APP_ENV_NAME_SHORT;

const ImprovementPlanList = ({ intl }) => {
  const dispatch = useDispatch();
  let { hazardEventId, siteId, entityId, segmentName, selectedTab } = useParams();
  const history = useHistory();
  const userProfileWidgets = useSelector(state => getUserProfileWidget(state));
  const improvementPlan = useSelector(state => getImprovementPlan(state));
  const generalData = useSelector(state => getGeneralData(state));
  segmentName = segmentName || 'control';
  selectedTab = selectedTab || '3';

  // //clean up
  useEffect(() => {
    return () => {
      dispatch(
        setImprovementPlanChangeThunk({
          ...initialImprovementPlanState.improvementPlan
        })
      );
    };
  }, []);

  const [active, setActive] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');
  const [improvementPlanId, setImprovementPlanId] = useState(0);
  const [filterStatusId, setFilterStatusId] = useState(0);
  const [asendingOrder, setAsendingOrder] = useState(true);

  let filter = improvementPlan.filterBy.filter || null;
  let offset = improvementPlan.offset || 0;
  let limit = improvementPlan.limit || 1000;

  let filterLocalStatusId = improvementPlan.filterBy.filterStatusId || null;
  let isOpSuccessful = improvementPlan.isOpSuccessful || false;

  useEffect(() => {
    dispatch(getFilterThunk('WQSP_IMPROVEMENT_PLAN_LIST', hazardEventId, siteId));
  }, [dispatch, getFilterThunk]);

  useEffect(() => {
    let filterObj = {
      filter: filter,
      filterStatusId: filterLocalStatusId,
      offset: offset,
      hazardEventId: hazardEventId,
      filterSiteId: siteId,
      limit: limit
    };
    dispatch(getImprovementPlanThunk(filterObj, 'WQSP_IMPROVEMENT_PLAN_LIST'));
  }, [getImprovementPlanThunk, filter, filterLocalStatusId]);

  let disableControls = (improvementPlan && improvementPlan.isLoading) || false;

  let messageId = (improvementPlan && improvementPlan.displayMessageCode) || 'none';
  const messageText = intl.formatMessage({ id: messageId, defaultMessage: messageId });

  const improvementPlanStatus = generalData.generalFieldTypes.filter(item => item.FieldType === GENERAL_STATUS);
  const statusOptions = () => {
    let statusData = [];

    improvementPlanStatus.forEach(element => {
      statusData.push({
        label: element.Name,
        value: element.GeneralFieldTypeId
      });
    });

    let sortedData = statusData.sort(compareValues('label'));
    sortedData.splice(0, 0, {
      label: <FormattedMessage id="wqsp.filters.allStatus" defaultMessage="All Status" />,
      value: 0
    });
    return sortedData;
  };

  const onPageChangeHandler = currentPage => {
    setCurrentPage(currentPage);
  };

  const onSortOrder = () => {
    setAsendingOrder(!asendingOrder);
  };

  const onStatusChange = e => {
    setFilterStatusId(e.value);
    filteredResults(STATUS, e.value);
    setCurrentPage(1);
  };

  const searchHandler = value => {
    setSearchFilter(value);
    filteredResults(SEARCH_FILTER, value);
    setCurrentPage(1);
  };

  const filteredResults = (filterType, value) => {
    let filteredList = improvementPlan.improvementPlans;

    let apifilterObj = {
      filter: null,
      filterStatusId: null
    };

    if (filterType === STATUS) {
      if (value !== 0) {
        apifilterObj.filterStatusId = value;
        filteredList = filteredList.filter(function(item) {
          return item.StatusId === value;
        });
      }
    } else {
      if (filterStatusId !== 0) {
        apifilterObj.filterStatusId = filterStatusId;
        filteredList = filteredList.filter(function(item) {
          return item.StatusId === filterStatusId;
        });
      }
    }

    if (filterType === SEARCH_FILTER) {
      if (value !== '') {
        apifilterObj.filter = value;
        filteredList = filteredList.filter(function(item) {
          return item.Name.toLowerCase().includes(value.toLowerCase());
        });
      }
    } else {
      if (searchFilter !== '') {
        apifilterObj.filter = searchFilter;
        filteredList = filteredList.filter(function(item) {
          return item.Name.toLowerCase().includes(searchFilter.toLowerCase());
        });
      }
    }

    if (improvementPlan.improvementPlanListCount > improvementPlan.improvementPlans.length) {
      dispatch(setPageFilter(apifilterObj));
    } else {
      dispatch(setFilteredDataThunk({ filteredResults: { filteredResults: filteredList } }));
    }
  };

  let improvementPlansObj = (improvementPlan && Object.values(improvementPlan.filteredResults)) || [];

  if (asendingOrder) {
    improvementPlansObj = improvementPlansObj.sort(compareValues('DateRaised'));
  } else {
    improvementPlansObj = improvementPlansObj.sort(compareValues('DateRaised', 'desc'));
  }

  const onAddNewClick = () => {
    if (segmentName === 'define') {
      history.push(
        `/site/${siteId}/${segmentName}/${selectedTab}/entities/entity/${entityId}/hazard-events/hazard-event-view/${hazardEventId}/improvement-plans/improvement-plan`
      );
    } else if (segmentName === 'identify') {
      history.push(
        `/site/${siteId}/${segmentName}/${selectedTab}/hazard-events/hazard-event-view/${hazardEventId}/improvement-plans/improvement-plan`
      );
    } else {
      history.push(`/site/${siteId}/${segmentName}/${selectedTab}/improvement-plans/improvement-plan`);
    }
  };

  const onViewClick = improvementPlanId => {
    if (segmentName === 'control') {
      history.push(`/site/${siteId}/${segmentName}/${selectedTab}/improvement-plans/improvement-plan-view/${improvementPlanId}`);
    } else {
      history.push(`/hazard-events/${hazardEventId}/improvement-plans/improvement-plan-view/${improvementPlanId}`);
    }

    if (segmentName === 'define') {
      history.push(
        `/site/${siteId}/${segmentName}/${selectedTab}/entities/entity/${entityId}/hazard-events/hazard-event-view/${hazardEventId}/improvement-plans/improvement-plan-view/${improvementPlanId}`
      );
    } else if (segmentName === 'identify') {
      history.push(
        `/site/${siteId}/${segmentName}/${selectedTab}/hazard-events/hazard-event-view/${hazardEventId}/improvement-plans/improvement-plan-view/${improvementPlanId}`
      );
    } else {
      history.push(`/site/${siteId}/${segmentName}/${selectedTab}/improvement-plans/improvement-plan-view/${improvementPlanId}`);
    }
  };

  const onRemoveClick = improvementPlanId => {
    setActive(true);
    setImprovementPlanId(improvementPlanId);
  };

  const handleClose = () => {
    setActive(false);
    setImprovementPlanId(0);
  };

  const handleConfirm = () => {
    deleteHazardEvent();
    handleClose();
  };

  const deleteHazardEvent = () => {
    let saveData = {};
    saveData.PerformDelete = true;
    saveData.ImprovementPlanId = improvementPlanId;
    saveData.UpdatedBy = Cookies.get(`userid-${envName}`) || 0;
    dispatch(saveImprovementPlanThunk(saveData, 'WQSP_IMPROVEMENT_PLAN_DELETE'));
  };

  const isViewAllowed = IsWidgetAccessible(userProfileWidgets, 'WQSP_IMPROVEMENT_PLAN_VIEW');
  const isDeleteAllowed = IsWidgetAccessible(userProfileWidgets, 'WQSP_IMPROVEMENT_PLAN_DELETE');
  const dialogTitle = intl.formatMessage({
    id: 'improvementPlan.confirmDelete',
    defaultMessage: 'Are you sure you want to delete this improvement plan?'
  });

  const hazardEventName = improvementPlansObj.length > 0 ? ' (' + improvementPlansObj[0].HazardEventName + ')' : '';
  let hazardEventHeading = intl.formatMessage({ id: 'hazardEvent.hazardEvent', defaultMessage: 'Hazard Event' }) + hazardEventName;

  const defineEntityCrumb = [
    {
      orderNo: 1,
      name: <FormattedMessage id="siteControlsTabs.entityListing" defaultMessage="List of Entities" />,
      url: `/site/${siteId}/${segmentName}/${selectedTab}/entities`,
      showlink: true,
      showCrumb: true
    },
    {
      orderNo: 2,
      name: <FormattedMessage id="hazardEvent.hazardEvents" defaultMessage="Hazard Events" />,
      url: `/site/${siteId}/${segmentName}/${selectedTab}/entities/entity/${entityId}/hazard-events`,
      showlink: true,
      showCrumb: true
    },
    {
      orderNo: 3,
      name: hazardEventHeading,
      url: `/site/${siteId}/${segmentName}/${selectedTab}/entities/entity/${entityId}/hazard-events/hazard-event-view/${hazardEventId}`,
      showlink: true,
      showCrumb: true
    },
    {
      orderNo: 4,
      name: <FormattedMessage id="improvementPlan.improvementPlans" defaultMessage="Improvement Plans" />,
      url: ``,
      showlink: false,
      showCrumb: true
    }
  ];

  const identifyHazardEventcrumb = [
    {
      orderNo: 1,
      name: <FormattedMessage id="hazardEvent.hazardEvents" defaultMessage="Hazard Events" />,
      url: `/site/${siteId}/${segmentName}/${selectedTab}/hazard-events`,
      showlink: true,
      showCrumb: true
    },
    {
      orderNo: 2,
      name: hazardEventHeading,
      url: `/site/${siteId}/${segmentName}/${selectedTab}/hazard-events/hazard-event-view/${hazardEventId}`,
      showlink: true,
      showCrumb: true
    },
    {
      orderNo: 3,
      name: <FormattedMessage id="improvementPlan.improvementPlans" defaultMessage="Improvement Plans" />,
      url: ``,
      showlink: false,
      showCrumb: true
    }
  ];

  let breadcrumb = identifyHazardEventcrumb;

  if (segmentName === 'define') {
    breadcrumb = defineEntityCrumb;
  }

  return (
    <div className={s.improvementPlanList}>
      <SiteDashboardControlsContentTemplate
        selectedPage={selectedTab}
        siteId={siteId}
        segmentName={segmentName}
        userProfileWidgets={userProfileWidgets}
      >
        {segmentName !== 'control' && <Breadcrumb data={breadcrumb} segmentName={segmentName} />}

        <ConfirmDialog title={dialogTitle} onConfirm={handleConfirm} onClose={handleClose} showDialog={active} />
        <div className={s.contentWrapper}>
          {improvementPlan.isLoading && <LoadingSpinner />}
          <Banner
            failureText={messageText}
            showBanner={improvementPlan.showBanner}
            status={improvementPlan.isOpSuccessful}
            successText={messageText}
          />
          <Row>
            <Col sm={6}>
              <span className={s.textHeading}>
                {' '}
                <FormattedMessage id="common.filterBy" defaultMessage="Filter By" />
              </span>
            </Col>
          </Row>
          <Row>
            <Col sm={6} lg={2}>
              <Dropdown
                id="drpStatus"
                dataArray={statusOptions()}
                controlData={{
                  placeholderText: <FormattedMessage id="common.status" defaultMessage="Status" />,
                  customClassName: ''
                }}
                onDropdownChange={e => onStatusChange(e)}
                selectedOption={statusOptions().filter(option => option.value === filterStatusId)}
                disabled={disableControls}
              />
            </Col>
            <Col sm={6} lg={4}>
              <SearchBar
                searchHandler={searchHandler}
                clearSearchInVisible={false}
                initialText={searchFilter}
                placeHolderTextId="common.search"
              />
            </Col>
            <Col sm={12} lg={6}>
              <Button
                className={s.addbutton}
                style={WidgetVisibility(userProfileWidgets, 'WQSP_IMPROVEMENT_PLAN_ADDNEW')}
                onClick={onAddNewClick}
                disabled={disableControls}
                data-unittest="addButton"
              >
                + <FormattedMessage id="common.addNew" defaultMessage="ADD NEW" />
              </Button>
            </Col>
          </Row>
          <div className="">
            <Pagination
              data={improvementPlansObj}
              pageSize={DEFAULT_PAGE_SIZE}
              onPageChange={onPageChangeHandler}
              startingPage={improvementPlan.currentPage}
            >
              <ImprovementPlanListTable
                onSortOrder={onSortOrder}
                asendingOrder={asendingOrder}
                viewImprovementPlan={onViewClick}
                removeImprovementPlan={onRemoveClick}
                viewAllowed={isViewAllowed}
                deleteAllowed={isDeleteAllowed}
              />
            </Pagination>
          </div>
        </div>
      </SiteDashboardControlsContentTemplate>
    </div>
  );
};

export default injectIntl(ImprovementPlanList);
